///
/// EarthBrowser
///
/// Created by Matt Giger on 10/31/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Thumbnail atlas for the webcam chips: one 2048x1024 RGBA texture of
/// 96x54 slots (16:9, enough for a ~45 px chip on a 2x display). Slots are
/// claimed as chips come on screen, filled from the CDN thumbnail (the same
/// cache-busted URL the hover card and browser use, so the browser cache
/// serves it) and reclaimed least-recently-drawn first when the atlas is
/// full. Thumbnails are cover-cropped into the slot through a scratch
/// canvas, which also tames the NDBC 2880x300 panoramas.
///

import { requestRender } from "@stovokor/engine/RenderRequest"
import { Texture } from "@stovokor/shader/Texture"
import { type StovokorContext } from "@stovokor/StovokorContext"

export const cThumbSlotWidth = 96
export const cThumbSlotHeight = 54
const cAtlasWidth = 2048
const cAtlasHeight = 1024
const cCols = Math.floor(cAtlasWidth / cThumbSlotWidth)
const cRows = Math.floor(cAtlasHeight / cThumbSlotHeight)
const cCapacity = cCols * cRows
// Concurrent thumbnail fetches
const cMaxInFlight = 6
// Fade-in after a slot lands
export const cThumbFadeMs = 250
// Mean channel value (0..1) under which a frame counts as a night capture
const cDarkLuminance = 0.13

/** Where a leaf's thumbnail is in the atlas and how far it has faded in */
export interface ThumbPlacement {
	u0: number
	v0: number
	u1: number
	v1: number
	/** 0 while loading, ramping to 1 over cThumbFadeMs once uploaded */
	fade: number
}

export class WebcamThumbAtlas {
	public readonly texture: Texture

	private readonly slotLeaf = new Int32Array(cCapacity).fill(-1)
	private readonly slotUsedAt = new Float64Array(cCapacity)
	// 0 = not (yet) loaded
	private readonly slotLoadedAt = new Float64Array(cCapacity)
	private readonly slotLoading = new Uint8Array(cCapacity)
	private readonly leafSlot = new Map<number, number>()
	private readonly failed = new Set<number>()
	// Leaves whose thumbnail decoded nearly black (a night frame); the
	// featured set skips these
	private readonly dark = new Set<number>()
	private readonly queue: Array<{ slot: number, leaf: number, url: string, generation: number }> = []
	private inFlight = 0
	// Bumped when the catalog (and so every leaf index) is replaced
	private generation = 0
	private scratch?: HTMLCanvasElement
	private readonly placement: ThumbPlacement = { u0: 0, v0: 0, u1: 0, v1: 0, fade: 0 }
	private loadedCount = 0

	constructor(context: StovokorContext) {
		this.texture = new Texture(
			context, cAtlasWidth, cAtlasHeight,
			WebGL2RenderingContext.RGBA, WebGL2RenderingContext.RGBA, WebGL2RenderingContext.UNSIGNED_BYTE,
			WebGL2RenderingContext.LINEAR, WebGL2RenderingContext.LINEAR
		)
	}

	public destroy(): void {
		this.texture.destroy()
	}

	/** Leaf indices changed (catalog reload): forget every placement */
	public reset(): void {
		this.generation++
		this.leafSlot.clear()
		this.failed.clear()
		this.dark.clear()
		this.queue.length = 0
		this.slotLeaf.fill(-1)
		this.slotLoadedAt.fill(0)
		this.slotLoading.fill(0)
		this.slotUsedAt.fill(0)
		this.loadedCount = 0
	}

	public get stats(): { capacity: number, used: number, loaded: number, queued: number, inFlight: number, failed: number } {
		return {
			capacity: cCapacity, used: this.leafSlot.size, loaded: this.loadedCount,
			queued: this.queue.length, inFlight: this.inFlight, failed: this.failed.size,
		}
	}

	/**
	 * The placement of a leaf's thumbnail, marking it drawn this frame so
	 * it isn't reclaimed; undefined while it has no slot. The returned object
	 * is reused between calls.
	 */
	public lookup(leaf: number, now: number): ThumbPlacement | undefined {
		const slot = this.leafSlot.get(leaf)
		if (slot === undefined) return undefined
		this.slotUsedAt[slot] = now
		const loadedAt = this.slotLoadedAt[slot]
		const p = this.placement
		const col = slot % cCols
		const row = Math.floor(slot / cCols)
		// Half-texel inset so linear filtering never bleeds the neighbor
		p.u0 = (col * cThumbSlotWidth + 0.5) / cAtlasWidth
		p.v0 = (row * cThumbSlotHeight + 0.5) / cAtlasHeight
		p.u1 = ((col + 1) * cThumbSlotWidth - 0.5) / cAtlasWidth
		p.v1 = ((row + 1) * cThumbSlotHeight - 0.5) / cAtlasHeight
		p.fade = loadedAt > 0 ? Math.min(1, (now - loadedAt) / cThumbFadeMs) : 0
		return p
	}

	/** Whether a leaf's thumbnail failed to load (so callers stop asking) */
	public hasFailed(leaf: number): boolean {
		return this.failed.has(leaf)
	}

	/** Whether a leaf's loaded thumbnail is a near-black (night) frame */
	public isDark(leaf: number): boolean {
		return this.dark.has(leaf)
	}

	/**
	 * Claim a slot for a leaf and start loading its thumbnail. No-op when
	 * it already has one or failed; silently skipped when every slot was
	 * drawn this frame (the caller retries next frame).
	 */
	public request(leaf: number, url: string, now: number): void {
		if (this.leafSlot.has(leaf) || this.failed.has(leaf)) return
		const slot = this.allocate(now)
		if (slot < 0) return
		this.slotLeaf[slot] = leaf
		this.slotUsedAt[slot] = now
		this.slotLoadedAt[slot] = 0
		this.slotLoading[slot] = 1
		this.leafSlot.set(leaf, slot)
		this.queue.push({ slot, leaf, url, generation: this.generation })
		this.pump()
	}

	private allocate(now: number): number {
		let victim = -1
		let oldest = Number.POSITIVE_INFINITY
		for (let s = 0; s < cCapacity; s++) {
			if (this.slotLeaf[s] < 0) return s
			if (this.slotLoading[s]) continue
			const usedAt = this.slotUsedAt[s]
			if (usedAt < now && usedAt < oldest) {
				oldest = usedAt
				victim = s
			}
		}
		if (victim >= 0) this.release(victim)
		return victim
	}

	private release(slot: number): void {
		const leaf = this.slotLeaf[slot]
		if (leaf >= 0) this.leafSlot.delete(leaf)
		if (this.slotLoadedAt[slot] > 0) this.loadedCount--
		this.slotLeaf[slot] = -1
		this.slotLoadedAt[slot] = 0
		this.slotLoading[slot] = 0
	}

	private pump(): void {
		while (this.inFlight < cMaxInFlight && this.queue.length > 0) {
			const job = this.queue.shift()!
			this.inFlight++
			void this.load(job.slot, job.leaf, job.url, job.generation).finally(() => {
				this.inFlight--
				this.pump()
			})
		}
	}

	private async load(slot: number, leaf: number, url: string, generation: number): Promise<void> {
		let bitmap: ImageBitmap | undefined
		try {
			const response = await fetch(url)
			if (!response.ok) throw new Error(`thumbnail ${response.status}`)
			const blob = await response.blob()
			bitmap = await createImageBitmap(blob)
			// The slot may have been reassigned or the catalog replaced while
			// the fetch was out
			if (generation !== this.generation || this.slotLeaf[slot] !== leaf) return
			const canvas = this.coverFit(bitmap)
			if (!canvas) throw new Error("no 2d context")
			if (this.isNearlyBlack(canvas)) this.dark.add(leaf)
			this.texture.uploadRegion(
				canvas,
				(slot % cCols) * cThumbSlotWidth,
				Math.floor(slot / cCols) * cThumbSlotHeight,
				cThumbSlotWidth,
				cThumbSlotHeight
			)
			this.slotLoadedAt[slot] = performance.now()
			this.slotLoading[slot] = 0
			this.loadedCount++
			requestRender()
		} catch {
			if (generation === this.generation && this.slotLeaf[slot] === leaf) {
				this.failed.add(leaf)
				this.release(slot)
			}
		} finally {
			bitmap?.close()
		}
	}

	/** Mean brightness of the slot-sized frame below cDarkLuminance: a night capture */
	private isNearlyBlack(canvas: HTMLCanvasElement): boolean {
		const ctx = canvas.getContext("2d", { willReadFrequently: true })
		if (!ctx) return false
		const data = ctx.getImageData(0, 0, cThumbSlotWidth, cThumbSlotHeight).data
		let sum = 0
		let n = 0
		// Every fourth pixel is plenty for a mean
		for (let i = 0; i < data.length; i += 16) {
			sum += data[i] + data[i + 1] + data[i + 2]
			n += 3
		}
		return sum / n < cDarkLuminance * 255
	}

	/** Scale the bitmap to fill the slot and center-crop the overflow */
	private coverFit(bitmap: ImageBitmap): HTMLCanvasElement | undefined {
		if (!this.scratch) {
			this.scratch = document.createElement("canvas")
			this.scratch.width = cThumbSlotWidth
			this.scratch.height = cThumbSlotHeight
		}
		const ctx = this.scratch.getContext("2d", { willReadFrequently: true })
		if (!ctx) return undefined
		const scale = Math.max(cThumbSlotWidth / bitmap.width, cThumbSlotHeight / bitmap.height)
		const cropW = cThumbSlotWidth / scale
		const cropH = cThumbSlotHeight / scale
		ctx.clearRect(0, 0, cThumbSlotWidth, cThumbSlotHeight)
		ctx.drawImage(bitmap, (bitmap.width - cropW) / 2, (bitmap.height - cropH) / 2, cropW, cropH, 0, 0, cThumbSlotWidth, cThumbSlotHeight)
		return this.scratch
	}
}
